import { ThemeProvider } from '@mui/material';
import { darkTheme, lightTheme } from '@style/MUI/Theming';
import { createContext, useEffect, useState, type ReactNode } from 'react';

export interface ThemeContextData {
    actions: { toggleTheme: () => void };
}

const data: ThemeContextData = { actions: { toggleTheme: () => { /* empty */ } } };

export const ThemeContext = createContext<ThemeContextData>(data);

// Default to dark mode if no setting can be found in localStorage
const getPrefersDarkMode = (): boolean => {
    return localStorage.ROWDarkMode == null || (localStorage.ROWDarkMode && localStorage.ROWDarkMode === 'true');
};

export function ThemeContextProvider({ children }: Readonly<{ children: ReactNode }>): React.ReactElement {
    const prefersDarkMode = getPrefersDarkMode();
    const chosenTheme = prefersDarkMode ? darkTheme : lightTheme;

    const [isDarkMode, setIsDarkMode] = useState(prefersDarkMode);
    const [currTheme, setCurrTheme] = useState(chosenTheme);

    useEffect(() => {
        const themeClassName = isDarkMode ? 'dark-theme' : 'light-theme';
        const themeClasses = themeClassName.split(' ');

        document.body.classList.remove('dark-theme', 'light-theme');
        document.body.classList.add(...themeClasses);
    }, [isDarkMode]);

    data.actions.toggleTheme = (): void => {
        setCurrTheme((prevTheme) => {
            const nextThemeIsDark = prevTheme === lightTheme;
            const nextTheme = nextThemeIsDark ? darkTheme : lightTheme;

            localStorage.ROWDarkMode = nextThemeIsDark;
            setIsDarkMode(nextThemeIsDark);
            return nextTheme;
        });
    };

    // Double dark-theme light-theme assignment for DobComponents
    const themeClassName = isDarkMode ? 'dark-theme' : 'light-theme';

    return (
        <ThemeContext.Provider value={data}>
            <ThemeProvider theme={currTheme}>
                <div className={themeClassName}>
                    {children}
                </div>
            </ThemeProvider>
        </ThemeContext.Provider>
    );
}
